import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { adminApiService } from "../../../service/admin/adminApiService";
import { fetchToken } from "../../../lib/globalApiClient";
import noImage from "../../../assets/images/common/importerr-no-image-logo.png";
import {
  FaSpinner, FaExternalLinkAlt, FaSync,
  FaDatabase, FaGlobe, FaStar, FaStore, FaBox, FaWeight, FaRuler
} from "react-icons/fa";
import { TbZoomScan } from "react-icons/tb";
import "./scraperStyle.css";

const BACKEND_API = process.env.REACT_APP_APIUrl || "https://api.importerr.com/api/";

const MapOrderScraper = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const asin = searchParams.get("asin") || "";
  const platform = searchParams.get("platform") || "amazon";
  const mapOrderId = searchParams.get("mapOrderId") || "";
  const offerIdFromUrl = searchParams.get("offerId") || "";
  const variantNameFromUrl = (searchParams.get("variantName") || "").trim();

  // Left panel
  const [leftLoading, setLeftLoading] = useState(false);
  const [leftError, setLeftError] = useState("");
  const [productData, setProductData] = useState(null);
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [activeTab, setActiveTab] = useState("description");

  // Right panel
  const [rightLoading, setRightLoading] = useState(false);
  const [rightError, setRightError] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [searchSource, setSearchSource] = useState("");
  const [rightImageIdx, setRightImageIdx] = useState(0);
  const [rightFailed, setRightFailed] = useState(false);

  // Find Similar
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);
  const [showSimilar, setShowSimilar] = useState(false);
  const [activeOfferId, setActiveOfferId] = useState(offerIdFromUrl); // tracks current offerId (may change via find similar)

  // Map modal
  const [mapModal, setMapModal] = useState({ open: false, variant: null });
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [resultModal, setResultModal] = useState({ open: false, success: false, message: "" });

  // Variant image hover preview
  const [varImgPreview, setVarImgPreview] = useState({ show: false, src: "", x: 0, y: 0 });

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      setCategoriesLoading(true);
      try {
        const token = localStorage.getItem("LOGIN_HEADER");
        const resp = await fetch(`${BACKEND_API}admin/categories/flat`, { headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" } });
        const result = await resp.json();
        if (resp.ok) {
          const cats = result.data?.categories || result.categories || result.data || [];
          const parsed = Array.isArray(cats) ? cats : [];
          console.log("[MapOrderScraper] Categories loaded:", parsed.length, parsed[0] ? Object.keys(parsed[0]) : "empty");
          setCategories(parsed);
        }
      } catch (e) { console.warn("Failed to fetch categories:", e); }
      finally { setCategoriesLoading(false); }
    };
    fetchCategories();
  }, []);

  const [categorySearch, setCategorySearch] = useState("");
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [isMapping, setIsMapping] = useState(false);

  const handleMapClick = (variant) => {
    // Single variant map
    setMapModal({ open: true, variant: null });
    setSelectedVariants([variant]);
    setSelectedCategory("");
    setCategorySearch("");
    setShowCategoryDropdown(false);
  };

  const handleBulkMapClick = () => {
    if (selectedVariants.length === 0) return;
    setMapModal({ open: true, variant: null });
    setSelectedCategory("");
    setCategorySearch("");
    setShowCategoryDropdown(false);
  };

  const toggleVariantSelection = (variant) => {
    const skuId = String(variant.skuId || variant.specId);
    setSelectedVariants(prev => {
      const exists = prev.find(v => String(v.skuId || v.specId) === skuId);
      if (exists) return prev.filter(v => String(v.skuId || v.specId) !== skuId);
      return [...prev, variant];
    });
  };

  const toggleSelectAll = (allVariants) => {
    if (selectedVariants.length === allVariants.length) {
      setSelectedVariants([]);
    } else {
      setSelectedVariants([...allVariants]);
    }
  };

  const isVariantSelected = (variant) => {
    const skuId = String(variant.skuId || variant.specId);
    return selectedVariants.some(v => String(v.skuId || v.specId) === skuId);
  };

  const filteredCategories = categories.filter(c => {
    if (!categorySearch) return true;
    const s = categorySearch.toLowerCase();
    return (c.path || "").toLowerCase().includes(s) || (c.c_name || "").toLowerCase().includes(s);
  });

  const handleMapConfirm = async () => {
    if (!selectedCategory || isMapping || selectedVariants.length === 0) return;
    setIsMapping(true);
    try {
      const token = localStorage.getItem("LOGIN_HEADER");
      let successCount = 0;
      let failCount = 0;

      for (const v of selectedVariants) {
        const skuId = v?.skuId || v?.specId || "";
        try {
          const resp = await fetch(`${BACKEND_API}admin/map-orders/map-asin`, {
            method: "POST",
            headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify({
              asin: asin,
              offerId: activeOfferId,
              skuId: String(skuId),
              categoryId: selectedCategory,
              categoryPath: categorySearch,
              mapOrderId: mapOrderId,
            }),
          });
          const result = await resp.json();
          if (resp.ok && result?.status?.code === 200) { successCount++; }
          else { failCount++; }
        } catch { failCount++; }
      }

      let msg = `Mapped ${successCount} variant(s) successfully`;
      if (failCount > 0) msg += `, ${failCount} failed`;
      setMapModal({ open: false, variant: null });
      setSelectedVariants([]);
      setResultModal({ open: true, success: failCount === 0, message: msg });
    } catch (err) {
      setResultModal({ open: true, success: false, message: "Mapping error: " + (err.message || "Unknown error") });
    } finally { setIsMapping(false); }
  };

  // Get logged-in user info
  const loggedInUser = (() => {
    try {
      const auth = JSON.parse(localStorage.getItem("auth") || "{}");
      return auth?.firstName ? `${auth.firstName} ${auth.lastName || ""}`.trim() : auth?.email || "Admin";
    } catch { return "Admin"; }
  })();

  // ==================== LEFT PANEL ====================
  const fetchLeftPanel = useCallback(() => {
    if (!asin) { setLeftError("No ASIN provided"); return; }
    setLeftLoading(true); setLeftError(""); setProductData(null); setActiveImageIdx(0);
    if (platform === "myntra") { fetchMyntra(); } else { fetchAmazon(); }
  }, [asin, platform]);

  const fetchAmazon = () => {
    adminApiService.getAmazonProductByAsin(asin)
      .then((res) => {
        if (res?.success && res?.data) { setProductData(res.data); if (res.data.error) setLeftError(res.data.error); }
        else { setLeftError(res?.message || "Failed to fetch product details"); }
      })
      .catch((err) => setLeftError(err?.message || "Failed to fetch product details"))
      .finally(() => setLeftLoading(false));
  };

  const fetchMyntra = async (forceReload = false) => {
    try {
      const token = localStorage.getItem("LOGIN_HEADER");
      const reloadParam = forceReload ? "?reload=true" : "";
      const resp = await fetch(`${BACKEND_API}admin/myntra-scrape/${asin}${reloadParam}`, { method: "GET", headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" } });
      const result = await resp.json();
      if (resp.ok && result?.success && result?.data) { setProductData(result.data); }
      else { setLeftError(result?.message || "Myntra product not found"); }
    } catch (err) { setLeftError("Failed to fetch Myntra product: " + (err.message || "")); }
    finally { setLeftLoading(false); }
  };

  useEffect(() => { fetchLeftPanel(); }, [fetchLeftPanel]);

  // ==================== RIGHT PANEL (auto-loads offerId) ====================
  const fetchRightPanel = useCallback(async () => {
    if (!offerIdFromUrl) { setRightError("No Offer ID provided"); return; }
    setRightLoading(true); setRightError(""); setSearchResult(null); setSearchSource(""); setRightImageIdx(0); setRightFailed(false);
    try {
      const token = localStorage.getItem("LOGIN_HEADER");
      const resp = await fetch(`${BACKEND_API}admin/product-by-offerid/${offerIdFromUrl.trim()}`, { method: "GET", headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" } });
      const result = await resp.json();
      if (resp.ok && result?.status?.code === 200 && result?.data?.product) {
        setSearchSource(result.data.source); setSearchResult(result.data.product); return;
      }
      const baseUrl = process.env.REACT_APP_APIUrl1688;
      if (baseUrl) {
        try {
          const apiToken = await fetchToken();
          const apiResp = await fetch(baseUrl.replace(/\/$/, "") + "/product-detail-by-offerid", { method: "POST", headers: { "Content-Type": "application/json", "X-API-Token": apiToken }, body: JSON.stringify({ offerId: offerIdFromUrl.trim(), country: "en" }) });
          if (apiResp.ok) {
            const data = await apiResp.json();
            const product = data.result?.result || data.product || data.data || data;
            if (product && (product.offerId || product.subject || product.title)) { setSearchSource("global_api"); setSearchResult(product); return; }
          }
        } catch (fallbackErr) { console.warn("Global API direct fallback failed:", fallbackErr); }
      }
      // Not found anywhere — mark as failed
      setRightFailed(true);
      setRightError(result?.error?.message || "Product not found in database or Global API");
      // Update MapOrder status to failed
      if (mapOrderId) {
        try {
          await fetch(`${BACKEND_API}admin/map-orders/${mapOrderId}/status`, {
            method: "PUT",
            headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify({ status: "failed" }),
          });
        } catch (e) { console.warn("Failed to update map order status:", e); }
      }
    } catch (err) { setRightError("Failed to fetch: " + (err.message || "")); setRightFailed(true); }
    finally { setRightLoading(false); }
  }, [offerIdFromUrl, mapOrderId]);

  useEffect(() => { if (offerIdFromUrl) fetchRightPanel(); }, [fetchRightPanel]);

  // ==================== HELPERS ====================
  const getLeftImages = () => {
    if (!productData) return [];
    if (platform === "myntra") { return (productData.images || []).map(i => (typeof i === "string" ? i : i?.url || "")).filter(Boolean); }
    if (productData.images?.length) return productData.images;
    if (productData.mainImage) return [productData.mainImage];
    return [];
  };
  const getProductSpec = (key) => {
    if (!productData) return null;
    const d = { ...productData.productDetails, ...productData.technicalDetails, ...productData.additionalInfo };
    for (const [k, v] of Object.entries(d || {})) { if (k.toLowerCase().includes(key.toLowerCase())) return v; }
    return null;
  };
  const leftImages = getLeftImages();
  const leftCurrentImage = leftImages[activeImageIdx] || noImage;

  // ==================== FIND SIMILAR ====================
  const handleFindSimilar = async () => {
    const imageUrl = leftImages[activeImageIdx] || leftImages[0];
    if (!imageUrl) return;
    setLoadingSimilar(true); setShowSimilar(true); setSimilarProducts([]);
    try {
      const proxyUrl = `${BACKEND_API.replace(/\/+$/, '')}/proxy/image?url=${encodeURIComponent(imageUrl)}`;
      const imgResp = await fetch(proxyUrl);
      if (!imgResp.ok) throw new Error("Failed to fetch image");
      const imgBlob = await imgResp.blob();
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(imgBlob);
      });
      const baseUrl = process.env.REACT_APP_APIUrl1688;
      const token = await fetchToken();
      const resp = await fetch(baseUrl.replace(/\/$/, "") + "/get-product-info-by-image", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-API-Token": token },
        body: JSON.stringify({ imageBase64: base64, beginPage: 1, pageSize: 12, country: "en" }),
      });
      if (!resp.ok) throw new Error("Image search API error");
      const result = await resp.json();
      const products = result.products || result.result?.result?.data || result.data || [];
      setSimilarProducts(products.slice(0, 12));
    } catch (err) {
      console.error("Find similar failed:", err);
      setSimilarProducts([]);
    } finally { setLoadingSimilar(false); }
  };

  const handleSelectSimilarProduct = (product) => {
    const newOfferId = product.offerId || product.id || "";
    if (!newOfferId) return;
    setShowSimilar(false); setRightFailed(false);
    setActiveOfferId(newOfferId); // Update active offerId to the new one
    setRightLoading(true); setRightError(""); setSearchResult(null); setSearchSource(""); setRightImageIdx(0);
    (async () => {
      try {
        const token = localStorage.getItem("LOGIN_HEADER");
        const resp = await fetch(`${BACKEND_API}admin/product-by-offerid/${newOfferId}`, { method: "GET", headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" } });
        const result = await resp.json();
        if (resp.ok && result?.status?.code === 200 && result?.data?.product) {
          setSearchSource(result.data.source); setSearchResult(result.data.product); return;
        }
        const baseUrl = process.env.REACT_APP_APIUrl1688;
        if (baseUrl) {
          const apiToken = await fetchToken();
          const apiResp = await fetch(baseUrl.replace(/\/$/, "") + "/product-detail-by-offerid", { method: "POST", headers: { "Content-Type": "application/json", "X-API-Token": apiToken }, body: JSON.stringify({ offerId: newOfferId, country: "en" }) });
          if (apiResp.ok) {
            const data = await apiResp.json();
            const p = data.result?.result || data.product || data.data || data;
            if (p && (p.offerId || p.subject || p.title)) { setSearchSource("global_api"); setSearchResult(p); return; }
          }
        }
        setRightError("Product not found for selected offer ID");
      } catch (err) { setRightError("Failed: " + err.message); }
      finally { setRightLoading(false); }
    })();
  };

  const getRightImages = () => {
    if (!searchResult) return [];
    if (searchSource === "database") {
      const imgs = (searchResult.imgUrl || []).map(i => (typeof i === "string" ? i : i?.url || i?.src || "")).filter(Boolean);
      if (searchResult.image && !imgs.includes(searchResult.image)) imgs.unshift(searchResult.image);
      if (searchResult.variations?.length) { searchResult.variations.forEach(v => { (v.skuAttributes || []).forEach(a => { if (a.skuImageUrl && !imgs.includes(a.skuImageUrl)) imgs.push(a.skuImageUrl); }); }); }
      return imgs;
    }
    return (searchResult.productImage?.images || searchResult.images || []).map(i => (typeof i === "string" ? i : i?.url || i?.imageUrl || "")).filter(Boolean);
  };
  const getRightTitle = () => searchResult?.title || searchResult?.name || searchResult?.subject || searchResult?.subjectTrans || searchResult?.productName || "";
  const getRightPrice = () => {
    if (!searchResult) return null;
    if (searchSource === "database") {
      const ranges = searchResult.priceRanges || [];
      // Get 1-qty price from first variation's ap field
      const oneQtyPrice = searchResult.variations?.length > 0
        ? searchResult.variations.reduce((min, v) => {
            const ap = parseFloat(v.ap || v.consignPrice || v.cp || 0);
            return ap > 0 && (min === 0 || ap < min) ? ap : min;
          }, 0)
        : 0;
      if (ranges.length > 0 || oneQtyPrice > 0) {
        return { type: "ranges", ranges, oneQtyPrice };
      }
      if (searchResult.vendor_price) return { type: "single", price: searchResult.vendor_price };
      return null;
    }
    // Global API
    const priceRange = searchResult.productSaleInfo?.priceRangeList || [];
    // Get 1-qty consign/dropship price from skuInfos
    const skus = searchResult.productSkuInfos || searchResult.skuInfos || [];
    let oneQtyPrice = 0;
    if (skus.length > 0) {
      oneQtyPrice = skus.reduce((min, s) => {
        const cp = parseFloat(
          s.consignPrice ||
          s.cp ||
          s.fenxiaoPriceInfo?.offerPrice ||
          s.fenxiaoPriceInfo?.onePiecePrice ||
          s.price ||
          0
        );
        return cp > 0 && (min === 0 || cp < min) ? cp : min;
      }, 0);
    }
    if (priceRange.length > 0 || oneQtyPrice > 0) {
      return { type: "ranges", ranges: priceRange, oneQtyPrice };
    }
    return null;
  };
  const rightImages = getRightImages();
  const rightCurrentImage = rightImages[rightImageIdx] || noImage;

  // ==================== RENDER ====================
  return (
    <div className="mos-page">
      <div className="mos-header">
        <h2 className="mos-title">Map Order Scraper - {platform === "myntra" ? "Myntra" : "Amazon"}</h2>
        <span className="mos-asin-badge">{platform === "myntra" ? "Product ID" : "ASIN"}: {asin}</span>
      </div>

      <div className="mos-split-container">
        {/* ===== LEFT PANEL ===== */}
        <div className="mos-panel mos-panel-left">
          <div className="mos-panel-header mos-panel-header-stacked">
            <h3>{platform === "myntra" ? "Myntra" : "Amazon"} Product Details</h3>
            <div className="mos-panel-header-actions">
              <button className="mos-reload-btn" onClick={fetchLeftPanel}><FaSync size={11} /> Reload</button>
              {platform === "myntra"
                ? <a href={`https://www.myntra.com/${asin}`} target="_blank" rel="noopener noreferrer" className="mos-external-link">View on Myntra <FaExternalLinkAlt size={11} /></a>
                : <a href={`https://www.amazon.in/dp/${asin}`} target="_blank" rel="noopener noreferrer" className="mos-external-link">View on Amazon <FaExternalLinkAlt size={11} /></a>}
            </div>
          </div>

          {leftLoading ? (
            <div className="mos-loading"><FaSpinner className="mos-spinner" /> Searching for product details...</div>
          ) : !productData && leftError ? (
            <div className="mos-error-box">
              <h4>Unable to load product</h4><p>{leftError}</p>
              <div className="mos-error-actions">
                <button className="mos-reload-btn" onClick={fetchLeftPanel}><FaSync size={11} /> Retry</button>
                {platform === "myntra"
                  ? <a href={`https://www.myntra.com/${asin}`} target="_blank" rel="noopener noreferrer" className="mos-external-link">View on Myntra <FaExternalLinkAlt size={11} /></a>
                  : <a href={`https://www.amazon.in/dp/${asin}`} target="_blank" rel="noopener noreferrer" className="mos-external-link">View on Amazon <FaExternalLinkAlt size={11} /></a>}
              </div>
            </div>
          ) : productData ? (
            <div className="mos-product-detail">
              {leftError && <div className="mos-partial-error">{leftError}</div>}
              <div className="mos-product-images">
                <div className="mos-main-image">
                  <img src={leftCurrentImage} alt="Product" onError={(e) => { e.target.src = noImage; }} />
                  {rightFailed && leftImages.length > 0 && (
                    <button className="mos-find-similar-icon" onClick={handleFindSimilar} disabled={loadingSimilar} title="Find Similar Products">
                      {loadingSimilar ? <FaSpinner className="mos-spinner" /> : <TbZoomScan size={20} />}
                    </button>
                  )}
                </div>
                {leftImages.length > 1 && <div className="mos-thumbnails">{leftImages.slice(0, 8).map((img, idx) => (
                  <img key={idx} src={img} alt="" className={`mos-thumb ${idx === activeImageIdx ? "mos-thumb-active" : ""}`} onClick={() => setActiveImageIdx(idx)} onError={(e) => { e.target.style.display = "none"; }} />
                ))}</div>}
              </div>
              <div className="mos-product-info">
                <h4 className="mos-product-title">{productData.title || productData.name || productData.productName || "Product title not available"}</h4>
                {productData.brand && <div className="mos-product-meta"><FaStore size={12} /> <span className="mos-brand">{productData.brand}</span> Store</div>}
                {productData.rating && <div className="mos-product-meta"><FaStar style={{ color: "#f59e0b" }} /> <span className="mos-brand">{productData.rating}</span>{productData.ratingCount && <span className="mos-meta-sub">({productData.ratingCount})</span>}</div>}
                <div className="mos-price-box">
                  {productData.discount && !String(productData.discount).includes("{") && <span className="mos-discount-badge">{productData.discount}</span>}
                  <div className="mos-price-main">{platform === "myntra" ? `₹${productData.price?.discounted || productData.price?.mrp || "-"}` : productData.price || "Price not available"}</div>
                  {productData.mrp && productData.mrp !== productData.price && <div className="mos-price-mrp">M.R.P.: <s>{productData.mrp}</s></div>}
                </div>
                {platform === "amazon" && (
                  <div className="mos-specs-grid">
                    <div className="mos-spec-item"><FaBox className="mos-spec-icon" /><div><span className="mos-spec-label">SKU/Model</span><span className="mos-spec-value">{getProductSpec("model") || getProductSpec("sku") || asin}</span></div></div>
                    <div className="mos-spec-item"><FaWeight className="mos-spec-icon" /><div><span className="mos-spec-label">Weight</span><span className="mos-spec-value">{getProductSpec("weight") || "N/A"}</span></div></div>
                    <div className="mos-spec-item"><FaRuler className="mos-spec-icon" /><div><span className="mos-spec-label">Dimensions</span><span className="mos-spec-value">{getProductSpec("dimension") || getProductSpec("size") || "N/A"}</span></div></div>
                    <div className="mos-spec-item"><FaStore className="mos-spec-icon" /><div><span className="mos-spec-label">Seller</span><span className="mos-spec-value">{productData.seller || "N/A"}</span></div></div>
                  </div>
                )}
                {platform === "myntra" && productData.sizes?.length > 0 && <div className="mos-sizes"><span className="mos-spec-label">Sizes: </span>{productData.sizes.map((s, i) => <span key={i} className={`mos-size-chip ${s.available ? "" : "mos-size-unavailable"}`}>{s.label}</span>)}</div>}
                {/* Myntra Colour Variants - Big Card Grid */}
                {platform === "myntra" && productData.colourVariants?.length > 0 && (
                  <div className="mos-colour-variants-section">
                    <h5 className="mos-colour-variants-title">Colour Variants <span>({productData.colourVariants.length})</span></h5>
                    <div className="mos-colour-variants-grid">
                      {productData.colourVariants.map((cv, idx) => (
                        <div
                          key={cv.styleId || idx}
                          className={`mos-colour-variant-card ${String(cv.styleId) === String(asin) ? "mos-colour-active" : ""}`}
                          onClick={() => {
                            if (cv.styleId && String(cv.styleId) !== String(asin)) {
                              window.open(`/admin/map-order-scraper?asin=${cv.styleId}&platform=myntra&mapOrderId=${mapOrderId}${offerIdFromUrl ? `&offerId=${offerIdFromUrl}` : ""}`, "_blank");
                            }
                          }}
                          title={cv.label || `Variant ${idx + 1}`}
                        >
                          <img src={cv.image || noImage} alt={cv.label || ""} className="mos-colour-variant-img" onError={(e) => { e.target.src = noImage; }} />
                          <div className="mos-colour-variant-info">
                            <span className="mos-colour-variant-label">{cv.label || `Variant ${idx + 1}`}</span>
                            {cv.styleId && <span className="mos-colour-variant-id">ID: {cv.styleId}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {platform === "amazon" && (
                  <div className="mos-tabs-section">
                    <div className="mos-tabs">
                      <button className={activeTab === "description" ? "mos-tab-active" : ""} onClick={() => setActiveTab("description")}>Description</button>
                      <button className={activeTab === "details" ? "mos-tab-active" : ""} onClick={() => setActiveTab("details")}>Product Details</button>
                      <button className={activeTab === "technical" ? "mos-tab-active" : ""} onClick={() => setActiveTab("technical")}>Technical</button>
                      <button className={activeTab === "reviews" ? "mos-tab-active" : ""} onClick={() => setActiveTab("reviews")}>Reviews</button>
                    </div>
                    <div className="mos-tab-content">
                      {activeTab === "description" && <div>{productData.features?.length > 0 ? <ul className="mos-features-list">{productData.features.map((f, i) => <li key={i}>{f}</li>)}</ul> : productData.description ? <p className="mos-desc-text">{String(productData.description).substring(0, 800)}</p> : <p className="mos-no-data">No description available</p>}</div>}
                      {activeTab === "details" && <div>{productData.productDetails && Object.keys(productData.productDetails).length > 0 ? <table className="mos-details-table"><tbody>{Object.entries(productData.productDetails).map(([k, v], i) => <tr key={i}><th>{k}</th><td>{v}</td></tr>)}</tbody></table> : <p className="mos-no-data">No product details available</p>}</div>}
                      {activeTab === "technical" && <div>{productData.technicalDetails && Object.keys(productData.technicalDetails).length > 0 ? <table className="mos-details-table"><tbody>{Object.entries(productData.technicalDetails).map(([k, v], i) => <tr key={i}><th>{k}</th><td>{v}</td></tr>)}</tbody></table> : <p className="mos-no-data">No technical details available</p>}</div>}
                      {activeTab === "reviews" && <div>{productData.topReviews?.length > 0 ? productData.topReviews.map((r, i) => <div key={i} className="mos-review-item"><div className="mos-review-header"><span className="mos-review-author">{r.author}</span>{r.verified && <span className="mos-verified-badge">Verified</span>}{r.rating && <span className="mos-review-rating">{r.rating}</span>}</div>{r.title && <div className="mos-review-title">{r.title}</div>}<p className="mos-review-text">{r.text}</p></div>) : <p className="mos-no-data">No reviews available</p>}</div>}
                    </div>
                  </div>
                )}
                {platform === "myntra" && productData.description && (
                  <div className="mos-product-desc">
                    <h5>Description</h5>
                    {productData.description.includes("\n") ? (
                      productData.description.split("\n").filter(Boolean).map((line, i) => {
                        const parts = line.split(":");
                        if (parts.length >= 2) {
                          return <div key={i} className="mos-desc-line"><span className="mos-desc-label">{parts[0].trim()}:</span> {parts.slice(1).join(":").trim()}</div>;
                        }
                        return <p key={i} className="mos-desc-text">{line.trim()}</p>;
                      })
                    ) : (
                      <p className="mos-desc-text">{productData.description.substring(0, 800)}</p>
                    )}
                  </div>
                )}
                {/* Myntra Product Details / Article Attributes */}
                {platform === "myntra" && productData.productDetails && typeof productData.productDetails === "object" && Object.keys(productData.productDetails).length > 0 && (
                  <div className="mos-myntra-details">
                    <h5>Product Details</h5>
                    <table className="mos-details-table">
                      <tbody>
                        {Object.entries(productData.productDetails).map(([k, v], i) => (
                          <tr key={i}><th>{k}</th><td>{v}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {/* Myntra Seller & Category */}
                {platform === "myntra" && (productData.seller || productData.category) && (
                  <div className="mos-meta-row" style={{ marginTop: 8 }}>
                    {productData.seller && <span className="mos-meta-chip"><FaStore size={10} /> Seller: <b>{productData.seller}</b></span>}
                    {productData.category && <span className="mos-meta-chip"><FaBox size={10} /> Category: <b>{productData.category}</b></span>}
                    {productData.colour && <span className="mos-meta-chip">Colour: <b>{productData.colour}</b></span>}
                  </div>
                )}
              </div>
            </div>
          ) : <div className="mos-empty-panel">No product data available</div>}
        </div>

        {/* ===== RIGHT PANEL (same layout as left, no search box) ===== */}
        <div className="mos-panel mos-panel-right">
          {variantNameFromUrl && (
            <div className="mos-variant-name-top-row" title={variantNameFromUrl}>
              Variant: {variantNameFromUrl}
            </div>
          )}
          <div className="mos-panel-header mos-panel-header-compact">
            <div className="mos-panel-header-left mos-panel-header-left-compact">
              <h3>Global Product Details</h3>
              {offerIdFromUrl && <span className="mos-asin-badge" style={{ background: activeOfferId !== offerIdFromUrl ? "#fee2e2" : "#dcfce7", color: activeOfferId !== offerIdFromUrl ? "#dc2626" : "#16a34a", fontSize: 10, textDecoration: activeOfferId !== offerIdFromUrl ? "line-through" : "none" }}>Offer ID: {offerIdFromUrl}</span>}
              {activeOfferId && activeOfferId !== offerIdFromUrl && <span className="mos-asin-badge" style={{ background: "#dcfce7", color: "#16a34a", fontSize: 10 }}>New: {activeOfferId}</span>}
              {searchResult && searchSource === "database" && <span className="mos-source-badge mos-source-db"><FaDatabase size={10} /> Found in Database</span>}
              {searchResult && searchSource !== "database" && <span className="mos-source-badge mos-source-api"><FaGlobe size={10} /> Fetched from Global API</span>}
            </div>
            <div className="mos-panel-header-actions mos-panel-header-actions-compact">
              {offerIdFromUrl && <button className="mos-reload-btn" onClick={fetchRightPanel}><FaSync size={11} /> Reload</button>}
              {offerIdFromUrl && <a href={`https://detail.1688.com/offer/${offerIdFromUrl}.html`} target="_blank" rel="noopener noreferrer" className="mos-external-link">View on Global <FaExternalLinkAlt size={11} /></a>}
            </div>
          </div>

          {!offerIdFromUrl ? (
            <div className="mos-empty-panel"><p>No Offer ID available for this map order</p><p className="mos-empty-sub">Upload an Excel with Offer IDs to see product details here</p></div>
          ) : rightLoading ? (
            <div className="mos-loading"><FaSpinner className="mos-spinner" /> Searching for product details...</div>
          ) : showSimilar ? (
            /* Similar Products Grid */
            <div className="mos-similar-section">
              <div className="mos-similar-header">
                <h4>Similar Products {similarProducts.length > 0 && `(${similarProducts.length})`}</h4>
                <button className="mos-reload-btn" onClick={() => setShowSimilar(false)}>✕ Close</button>
              </div>
              {loadingSimilar ? (
                <div className="mos-loading"><FaSpinner className="mos-spinner" /> Searching for similar products...</div>
              ) : similarProducts.length > 0 ? (
                <div className="mos-similar-grid">
                  {similarProducts.map((p, i) => {
                    const img = p.imageUrl || p.image || p.productImage?.images?.[0] || noImage;
                    const title = p.subjectTrans || p.subject || p.title || "Product";
                    const price = p.priceInfo?.price || p.price || "-";
                    const oid = p.offerId || p.id || "";
                    return (
                      <div key={i} className="mos-similar-card" onClick={() => handleSelectSimilarProduct(p)}>
                        <img src={img} alt="" className="mos-similar-img" onError={(e) => { e.target.src = noImage; }} />
                        <div className="mos-similar-info">
                          <p className="mos-similar-title">{title.substring(0, 60)}</p>
                          <span className="mos-similar-price">¥{price}</span>
                          {oid && <span className="mos-similar-oid">ID: {oid}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="mos-empty-panel">No similar products found</div>
              )}
            </div>
          ) : !searchResult && rightError ? (
            <div className="mos-error-box">
              <h4>Unable to load product</h4><p>{rightError}</p>
              <div className="mos-error-actions">
                <button className="mos-reload-btn" onClick={fetchRightPanel}><FaSync size={11} /> Retry</button>
                <a href={`https://detail.1688.com/offer/${offerIdFromUrl}.html`} target="_blank" rel="noopener noreferrer" className="mos-external-link">View on Global <FaExternalLinkAlt size={11} /></a>
              </div>
            </div>
          ) : searchResult ? (
            <div className="mos-product-detail">

              {/* Images - same layout as left panel */}
              <div className="mos-product-images">
                <div className="mos-main-image"><img src={rightCurrentImage} alt="Product" onError={(e) => { e.target.src = noImage; }} /></div>
                {rightImages.length > 1 && <div className="mos-thumbnails">{rightImages.slice(0, 8).map((img, idx) => (
                  <img key={idx} src={img} alt="" className={`mos-thumb ${idx === rightImageIdx ? "mos-thumb-active" : ""}`} onClick={() => setRightImageIdx(idx)} onError={(e) => { e.target.style.display = "none"; }} />
                ))}</div>}
              </div>

              {/* Product Info */}
              <div className="mos-product-info">
                <h4 className="mos-product-title">{getRightTitle()}</h4>

                {/* Price - 1688 style: big price on top, qty range below */}
                {(() => {
                  const priceData = getRightPrice();
                  if (!priceData) return null;
                  if (priceData.type === "ranges") {
                    return (
                      <div className="mos-price-ranges">
                        {priceData.oneQtyPrice > 0 && (
                          <div className="mos-price-range-item mos-price-range-single">
                            <div className="mos-price-range-value">¥{priceData.oneQtyPrice}</div>
                            <div className="mos-price-range-qty">1 qty</div>
                          </div>
                        )}
                        {priceData.ranges.map((r, i) => (
                          <div key={i} className="mos-price-range-item">
                            <div className="mos-price-range-value">¥{r.price}</div>
                            <div className="mos-price-range-qty">{r.startQuantity}+</div>
                          </div>
                        ))}
                      </div>
                    );
                  }
                  if (priceData.type === "single") {
                    return (
                      <div className="mos-price-ranges">
                        <div className="mos-price-range-item mos-price-range-single">
                          <div className="mos-price-range-value">¥{priceData.price}</div>
                          <div className="mos-price-range-qty">1 qty</div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* DB source details */}
                {searchSource === "database" && (
                  <>
                    {/* SKU, OfferId, MOQ in one row */}
                    <div className="mos-meta-row">
                      {searchResult.sku && <span className="mos-meta-chip"><FaBox size={10} /> SKU: <b>{searchResult.sku}</b></span>}
                      {searchResult.offerId && <span className="mos-meta-chip">Offer ID: <b>{searchResult.offerId}</b></span>}
                      {searchResult.moq && <span className="mos-meta-chip">MOQ: <b>{searchResult.moq}</b></span>}
                    </div>

                    {/* Variations Table - BEFORE description */}
                    {searchResult.variations?.length > 0 && (
                      <div className="mos-variants-section">
                        <div className="mos-variants-header">
                          <h5 className="mos-variants-title">Variations ({searchResult.variations.length})</h5>
                        </div>
                        <div className="mos-variants-table-wrap">
                          <table className="mos-variants-table">
                            <thead><tr>
                              <th>Image</th><th>SKU ID</th><th>Attributes</th><th>Price (¥)</th><th>Weight</th><th></th>
                            </tr></thead>
                            <tbody>
                              {searchResult.variations.map((v, i) => {
                                const varImg = v.skuAttributes?.find(a => a.skuImageUrl)?.skuImageUrl || "";
                                const attrs = (v.skuAttributes || []).map(a => a.valueTrans || a.value || "").filter(Boolean).join(", ");
                                return (
                                  <tr key={i}>
                                    <td>{varImg ? <img src={varImg} alt="" className="mos-var-img" onError={(e) => { e.target.style.display = "none"; }} onMouseEnter={(e) => { const rect = e.target.getBoundingClientRect(); const previewH = 362; const top = Math.min(Math.max(rect.top - previewH / 2 + 20, 10), window.innerHeight - previewH - 10); const left = Math.max(rect.left - 375, 10); setVarImgPreview({ show: true, src: varImg, x: left, y: top }); }} onMouseLeave={() => setVarImgPreview({ show: false, src: "", x: 0, y: 0 })} /> : "-"}</td>
                                    <td>{v.skuId || "-"}</td>
                                    <td>{attrs || "-"}</td>
                                    <td>{v.consignPrice || v.cp || "-"}</td>
                                    <td>{v.weight ? `${v.weight}g` : "-"}</td>
                                    <td><button className="mos-map-btn" onClick={() => handleMapClick(v)}>Map</button></td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Description - AFTER variations */}
                    {searchResult.description && <div className="mos-product-desc"><h5>Description</h5><p>{searchResult.description.substring(0, 400)}</p></div>}
                  </>
                )}

                {/* Global API source details */}
                {searchSource !== "database" && (
                  <>
                    <div className="mos-meta-row">
                      {searchResult.sellerLoginId && <span className="mos-meta-chip"><FaStore size={10} /> Seller: <b>{searchResult.sellerLoginId}</b></span>}
                      {(searchResult.moq || searchResult.minOrderQuantity) && <span className="mos-meta-chip">MOQ: <b>{searchResult.moq || searchResult.minOrderQuantity}</b></span>}
                      {(searchResult.productSkuInfos?.length || searchResult.skuInfos?.length) > 0 && <span className="mos-meta-chip">Variants: <b>{searchResult.productSkuInfos?.length || searchResult.skuInfos?.length}</b></span>}
                    </div>
                    {searchResult.companyName && <div className="mos-product-meta">Company: <span className="mos-brand">{searchResult.companyName}</span></div>}

                    {/* Variants Table for Global API */}
                    {(() => {
                      const skus = searchResult.productSkuInfos || searchResult.skuInfos || [];
                      if (skus.length === 0) return null;
                      return (
                        <div className="mos-variants-section">
                          <div className="mos-variants-header">
                            <h5 className="mos-variants-title">Variations ({skus.length})</h5>
                          </div>
                          <div className="mos-variants-table-wrap">
                            <table className="mos-variants-table">
                              <thead><tr>
                                <th>Image</th><th>SKU ID</th><th>Attributes</th><th>Price (¥)</th><th>Stock</th><th></th>
                              </tr></thead>
                              <tbody>
                                {skus.map((sku, i) => {
                                  const attrs = (sku.skuAttributes || []).map(a => a.valueTrans || a.value || "").filter(Boolean).join(", ");
                                  const skuImg = (sku.skuAttributes || []).find(a => a.skuImageUrl)?.skuImageUrl || "";
                                  const skuPrice = sku.consignPrice || sku.cp || sku.price || sku.fenxiaoPriceInfo?.offerPrice || sku.fenxiaoPriceInfo?.onePiecePrice || "-";
                                  const stock = sku.amountOnSale || sku.canBookCount || "-";
                                  return (
                                    <tr key={i}>
                                      <td>{skuImg ? <img src={skuImg} alt="" className="mos-var-img" onError={(e) => { e.target.style.display = "none"; }} onMouseEnter={(e) => { const rect = e.target.getBoundingClientRect(); const previewH = 362; const top = Math.min(Math.max(rect.top - previewH / 2 + 20, 10), window.innerHeight - previewH - 10); const left = Math.max(rect.left - 375, 10); setVarImgPreview({ show: true, src: skuImg, x: left, y: top }); }} onMouseLeave={() => setVarImgPreview({ show: false, src: "", x: 0, y: 0 })} /> : "-"}</td>
                                      <td>{sku.skuId || sku.specId || "-"}</td>
                                      <td>{attrs || "-"}</td>
                                      <td>¥{skuPrice}</td>
                                      <td>{stock}</td>
                                      <td><button className="mos-map-btn" onClick={() => handleMapClick(sku)}>Map</button></td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          ) : <div className="mos-empty-panel">No product data available</div>}
        </div>
      </div>

      {/* Map Modal */}
      {mapModal.open && (() => {
        return (
          <div className="mos-modal-overlay" onClick={() => setMapModal({ open: false, variant: null })}>
            <div className="mos-map-modal" onClick={(e) => e.stopPropagation()}>
              <div className="mos-map-modal-header">
                <h3>Confirm Mapping</h3>
                <button className="mos-map-modal-close" onClick={() => setMapModal({ open: false, variant: null })}>×</button>
              </div>

              {/* Mapping Summary */}
              <div className="mos-map-summary">
                <span className="mos-map-summary-label">MAPPING SUMMARY</span>
                <div className="mos-map-summary-row">
                  ASIN: <b>{asin}</b> &nbsp; Offer ID: <b>{activeOfferId}</b> &nbsp; Variants: <b>{selectedVariants.length}</b>
                </div>
              </div>

              {/* Selected Variants */}
              <div className="mos-map-variant-card">
                <span className="mos-map-variant-label">SELECTED VARIANT{selectedVariants.length > 1 ? "S" : ""} ({selectedVariants.length})</span>
                <div className="mos-map-variants-list">
                  {selectedVariants.map((v, i) => {
                    const vImg = (v.skuAttributes || []).find(a => a.skuImageUrl)?.skuImageUrl || "";
                    const vAttrs = (v.skuAttributes || []).map(a => a.valueTrans || a.value || "").filter(Boolean).join(", ");
                    const vPrice = v.consignPrice || v.cp || v.fenxiaoPriceInfo?.offerPrice || v.price || v.ap || "-";
                    const vSkuId = v.skuId || v.specId || "-";
                    return (
                      <div key={i} className="mos-map-variant-row">
                        {vImg && <img src={vImg} alt="" className="mos-map-variant-img-sm" onError={(e) => { e.target.style.display = "none"; }} />}
                        <span className="mos-map-chip">SKU: {vSkuId}</span>
                        {vAttrs && <span className="mos-map-chip">{vAttrs}</span>}
                        <span className="mos-map-rmb-price">¥{vPrice}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Category */}
              <div className="mos-map-field-group">
                <label>Category <span className="mos-required">*</span></label>
                <input
                  type="text"
                  className="mos-map-input"
                  placeholder="Search category..."
                  value={categorySearch}
                  onChange={(e) => { setCategorySearch(e.target.value); setSelectedCategory(""); setShowCategoryDropdown(true); }}
                  onFocus={() => setShowCategoryDropdown(true)}
                />
                {categoriesLoading ? (
                  <div className="mos-loading" style={{ padding: 8, fontSize: 12 }}><FaSpinner className="mos-spinner" /> Loading categories...</div>
                ) : showCategoryDropdown && !selectedCategory ? (
                  <div className="mos-category-dropdown">
                    {filteredCategories.slice(0, 50).map((cat, i) => (
                      <div
                        key={cat._id || cat.id || i}
                        className="mos-category-option"
                        onClick={() => {
                          const catId = cat._id || cat.id || cat.c_id || cat.node_id || cat.path;
                          setSelectedCategory(String(catId));
                          setCategorySearch(cat.path || cat.c_name);
                          setShowCategoryDropdown(false);
                        }}
                      >
                        <span className="mos-category-path">{cat.path || cat.c_name}</span>
                      </div>
                    ))}
                    {filteredCategories.length === 0 && <div className="mos-category-empty">No categories found</div>}
                  </div>
                ) : null}
                {!selectedCategory && !categorySearch && <span className="mos-field-error">Category is required</span>}
              </div>

              {/* Mapped By */}
              <div className="mos-map-field-group">
                <label>Mapped By</label>
                <div className="mos-map-user">{loggedInUser}</div>
              </div>

              {/* Actions */}
              <div className="mos-map-modal-actions">
                <button className="mos-modal-cancel" onClick={() => setMapModal({ open: false, variant: null })} disabled={isMapping}>Cancel</button>
                <button className="mos-modal-confirm" onClick={handleMapConfirm} disabled={!selectedCategory || isMapping}>
                  {isMapping ? <><FaSpinner className="mos-spinner" /> Mapping...</> : "Confirm & Map"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Result Modal */}
      {resultModal.open && (
        <div className="mos-modal-overlay">
          <div className="mos-result-modal">
            <div className={`mos-result-icon ${resultModal.success ? "mos-result-success" : "mos-result-error"}`}>
              {resultModal.success ? "✓" : "✕"}
            </div>
            <h3>{resultModal.success ? "Mapping Successful" : "Mapping Failed"}</h3>
            <p>{resultModal.message}</p>
            <div className="mos-result-actions">
              {resultModal.success && (
                <button className="mos-result-btn-primary" onClick={() => { window.location.href = "/admin/map-orders"; }}>
                  Go to Map Orders
                </button>
              )}
              <button className={resultModal.success ? "mos-result-btn-secondary" : "mos-result-btn-primary"} onClick={() => setResultModal({ open: false, success: false, message: "" })}>
                {resultModal.success ? "Stay on Page" : "Close"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Variant Image Hover Preview */}
      {varImgPreview.show && (
        <div className="mos-var-img-preview" style={{ top: varImgPreview.y, left: varImgPreview.x }}>
          <img src={varImgPreview.src} alt="Preview" />
        </div>
      )}
    </div>
  );
};

export default MapOrderScraper;
